<template>
  <section class="section-grid">

    <CampaignsKeywordsGridHeadline :columns="campaignsGridColumns" @keywords-close="onAdClose()" @select-columns="onColumnsSelect" />

    <mq-layout class="grid-summary-m" :mq="['sm', 'md']">
      <div class="grid-summary-inner">
        <span class="summary-label">{{ $t("Grid.TotalRecordsCount") }}:</span> <span class="summary-val">{{ campaigns ? campaigns.Total : 0 }}</span>
      </div>
      <b-input :value="searchValue" :placeholder="$t('Grid.Search')" @blur="onSearchChange($event)" @keyup.native.enter="onSearchChange($event)"
            v-show="showSearchInput"></b-input>
      <b-button class="is-transparent grid-search-m" @click="showSearchInput = true">
        <icon-base width="17" height="17" view-box="0 0 15 15" icon-name="search"><icon-search /></icon-base>
      </b-button>
    </mq-layout>
    <CampaignsKeywordsGridContent
      :columns="campaignsGridColumnsFiltered"
      :keywords="keywords"
      @toggle-google-campaign-details="emitToggleGoogleCampaignDetails"
    />
  </section>
</template>

<script lang="ts">
import VueMq from "vue-mq";
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import { Action, Getter } from "vuex-class";
import i18n from "@/i18n";
import { DataSourceResult } from "@/models/DataSourceResult";
import { GridColumn } from "@/models/GridColumn";
import { TableSettings, TableSettingsStateColumn } from "@/models/TableSettings";
import { CampaignsPageViewModel } from "@/models/campaigns/Campaign";
import { CampaignKeywordGridViewModel } from "@/models/campaigns/CampaignKeywordGridViewModel";
import CampaignsKeywordsGridContent from "./CampaignsKeywordsGridContent.vue";
import CampaignsKeywordsGridHeadline from "./CampaignsKeywordsGridHeadline.vue";
import IconBase from "../IconBase.vue";
import IconCaretDown from "../icons/IconCaretDown.vue";
import IconCaretLeft from "../icons/IconCaretLeft.vue";
import IconCaretRight from "../icons/IconCaretRight.vue";
import IconSearch from "../icons/IconSearch.vue";

Vue.use(VueMq, {
  breakpoints: {
    sm: 576,
    md: 992,
    lg: Infinity,
  },
  defaultBreakpoint: "sm",
});
const namespace = "campaignsModule";

@Component({
  components: {
    CampaignsKeywordsGridContent,
    CampaignsKeywordsGridHeadline,
    IconBase,
    IconSearch,
    IconCaretDown,
    IconCaretRight,
    IconCaretLeft,
  },
})
export default class CampaignsKeywordsGrid extends Vue {
  @Prop() readonly keywords!: CampaignKeywordGridViewModel[] | null;
  
  //#region Vuex getters and actions

  @Getter("campaigns", { namespace }) campaigns!: DataSourceResult;
  @Getter("campaignsPageData", { namespace }) campaignsPageData!: CampaignsPageViewModel;
  @Getter("searchValue", { namespace }) searchValue!: string;
  @Getter("tableSettings", { namespace }) tableSettings!: TableSettings[];
  @Action("setSearchValue", { namespace }) setSearchValue!: (searchValue: string) => void;

  //#endregion Vuex getters and actions


  //#region Properties

  gridId = "ad_groups__keywords";
  selectedColumnsIds: number[] = [];

  campaignsGridColumns: GridColumn[] = [
    {
      id: 1,
      name: "Actions",
      label: i18n.t("Campaigns.AdGroupsGrid.Columns.Actions").toString(),
      searchable: false,
      sortable: true,
      visible: true,
      forbidden: false,
    },
    { id: 2, name: "KeywordId", label: i18n.t("Campaigns.KeywordsGrid.Columns.KeywordId").toString(), class: "cell-keyword-id", searchable: true, sortable: true, visible: false, forbidden: false, width: 300 },
    { id: 3, name: "Keyword", label: i18n.t("Campaigns.KeywordsGrid.Columns.Keyword").toString(), class: "cell-keyword", searchable: true, sortable: true, visible: true, forbidden: false, width: 150 },
    { id: 4, name: "Impressions", label: i18n.t("Campaigns.KeywordsGrid.Columns.Impressions").toString(), class: "cell-keyword-Impressions", searchable: true, sortable: true, visible: true, forbidden: false, width: 150 },
    { id: 5, name: "Clicks", label: i18n.t("Campaigns.KeywordsGrid.Columns.Clicks").toString(), class: "cell-keyword-clicks", searchable: true, sortable: true, visible: true, forbidden: false, width: 150 },
    { id: 6, name: "ClickRate", label: i18n.t("Campaigns.KeywordsGrid.Columns.ClickRate").toString(), class: "cell-keyword-click-rate", searchable: true, sortable: true, visible: true, forbidden: false, width: 150 },
    { id: 7, name: "Spent", label: i18n.t("Campaigns.KeywordsGrid.Columns.Spent").toString(), class: "cell-keyword-spent", searchable: true, sortable: true, visible: true, forbidden: false, width: 150 },
    { id: 8, name: "LeadsConversion", label: i18n.t("Campaigns.KeywordsGrid.Columns.LeadsConversion").toString(), class: "cell-keyword-leads-conversion", searchable: true, sortable: true, visible: true, forbidden: false, width: 150 },
    { id: 9, name: "FormLeads", label: i18n.t("Campaigns.KeywordsGrid.Columns.FormLeads").toString(), class: "cell-keyword-form-leads", searchable: true, sortable: true, visible: true, forbidden: false, width: 150 },
    { id: 10, name: "PhoneLeads", label: i18n.t("Campaigns.KeywordsGrid.Columns.PhoneLeads").toString(), class: "cell-keyword-phone-leads", searchable: true, sortable: true, visible: true, forbidden: false, width: 150 },
    { id: 11, name: "TotalLeads", label: i18n.t("Campaigns.KeywordsGrid.Columns.TotalLeads").toString(), class: "cell-keyword-total-leads", searchable: true, sortable: true, visible: true, forbidden: false, width: 150 },
    { id: 12, name: "LeadPrice", label: i18n.t("Campaigns.KeywordsGrid.Columns.LeadPrice").toString(), class: "cell-keyword-lead-price", searchable: true, sortable: true, visible: true, forbidden: false, width: 150 },
    { id: 13, name: "DesktopLeads", label: i18n.t("Campaigns.KeywordsGrid.Columns.DesktopLeads").toString(), class: "cell-keyword-desktop-leads", searchable: true, sortable: true, visible: true, forbidden: false, width: 150 },
    { id: 14, name: "MobileLeads", label: i18n.t("Campaigns.KeywordsGrid.Columns.MobileLeads").toString(), class: "cell-keyword-mobile-leads", searchable: true, sortable: true, visible: true, forbidden: false, width: 150 },
    { id: 15, name: "Meetings", label: i18n.t("Campaigns.KeywordsGrid.Columns.Meetings").toString(), class: "cell-keyword-meetings", searchable: true, sortable: true, visible: true, forbidden: false, width: 150 },
    { id: 16, name: "MeetingPrice", label: i18n.t("Campaigns.KeywordsGrid.Columns.MeetingPrice").toString(), class: "cell-keyword-meeting-price", searchable: true, sortable: true, visible: true, forbidden: false, width: 150 },
    { id: 17, name: "Deals", label: i18n.t("Campaigns.KeywordsGrid.Columns.Deals").toString(), class: "cell-keyword-deals", searchable: true, sortable: true, visible: true, forbidden: false, width: 150 },
    { id: 18, name: "DealAmount", label: i18n.t("Campaigns.KeywordsGrid.Columns.DealAmount").toString(), class: "cell-keyword-deal-amount", searchable: true, sortable: true, visible: true, forbidden: false, width: 150 },
    { id: 19, name: "ROI", label: i18n.t("Campaigns.KeywordsGrid.Columns.ROI").toString(), class: "cell-keyword-average-roi", searchable: true, sortable: true, visible: true, forbidden: false, width: 150 },
    { id: 20, name: "DealsConversion", label: i18n.t("Campaigns.KeywordsGrid.Columns.DealsConversion").toString(), class: "cell-keyword-deals-conversion", searchable: true, sortable: true, visible: true, forbidden: false, width: 150 },
  ];

  showSearchInput = false;

  //#endregion Properties


  //#region Watchers

  @Watch("tableSettings", { immediate: true })
  onTableSettingsChange(newValue: TableSettings[], oldValue: TableSettings[]): void {
    if (newValue === undefined) {
      return;
    } else {
      let settings: TableSettings | undefined = newValue.find((i: TableSettings) => i.GridId === this.gridId);
      if (settings) {
        this.updateColumns(settings);
      }
    }
  }

  @Watch("campaignsPageData", { immediate: true })
  onCampaignsPageDataChange(newValue: CampaignsPageViewModel, oldValue: CampaignsPageViewModel): void {
    if (oldValue === undefined || newValue === null) {
      return;
    } else {
      let settings: TableSettings | undefined = this.tableSettings.find((i: TableSettings) => i.GridId === this.gridId);
      if (settings) {
        this.updateColumns(settings);
      }
    }
  }

  //#endregion Watchers


  //#region Getters

  get campaignsGridColumnsFiltered(): GridColumn[] {
    // if no selected columns (like on first page load), select all columns
    if (this.selectedColumnsIds.length === 0) {
      for (let i = 0; i < this.campaignsGridColumns.length; i++) {
        if (this.campaignsGridColumns[i].visible && !this.campaignsGridColumns[i].forbidden) {
          this.selectedColumnsIds.push(this.campaignsGridColumns[i].id);
        }
      }
    }
    for (let i = 0; i < this.campaignsGridColumns.length; i++) {
      this.campaignsGridColumns[i].visible = this.selectedColumnsIds.indexOf(this.campaignsGridColumns[i].id) >= 0;
    }
    return this.campaignsGridColumns;
  }

  //#endregion Getters
  

  //#region Handlers

  onAdClose() {
    this.$emit("keywords-close");
  }

  onColumnsSelect(selectedColumnsIds: number[]) {
    this.selectedColumnsIds = selectedColumnsIds;
  }

  onSearchChange(e: Event) {
    this.setSearchValue((e.target as HTMLInputElement).value);
  }

  emitToggleGoogleCampaignDetails(): void {
    this.$emit("toggle-google-campaign-details");
  }

  //#endregion Handlers


  //#region Private methods

  private updateColumns(tableSettings: TableSettings) {
    for (let i = 0; i < this.campaignsGridColumns.length; i++) {
      const tableSettingsColumn: TableSettingsStateColumn | undefined = tableSettings?.StateObject.columns.find((column: TableSettingsStateColumn) => {
        let columnName: string | undefined = column.field;
        if (!columnName) {
          columnName = column.title;
        }
        switch (columnName) {
          case "Actions":
          case "פעולות":
            columnName = "Actions"
            break;
        }
        return columnName === this.campaignsGridColumns[i].name;
      });

      this.campaignsGridColumns[i].visible = !tableSettingsColumn || tableSettingsColumn && !tableSettingsColumn.hidden;
    }
  }

  //#endregion Private methods
}
</script>

<style lang="scss" scoped>
@import "@/assets/styles/variables";

.section-grid {
  background: #fff;
  border: 1px solid #d3d6e2;
  border-radius: 4px;
  margin-bottom: 30px;
}
.grid-bottom {
  padding: 9px 15px 11px;
  .grid-bottom-inner {
    display: flex;
    justify-content: space-between;
  }
  .per-page {
    .per-page-label {
      font-weight: 600;
    }
    .per-page-dropdown {
      .button-text-w {
        margin-left: 7px;
      }
      a.dropdown-item,
      .dropdown .dropdown-menu .has-link a,
      .dropdown .dropdown-menu .has-link a,
      button.dropdown-item {
        padding: 3px 5px;
      }
    }
  }
  .grid-show {
    display: flex;
    .show-text {
      margin-left: 5px;
    }
    .show-nav {
      display: flex;
      align-items: center;
      .show-nav-button {
        padding: 0;
        width: 30px;
        height: 20px;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        &:hover {
          color: $link;
        }
        &.show-nav-button-prev {
        }
        &.show-nav-button-next {
        }
      }
    }
  }
}
.grid-summary {
  padding: 8px 23px 9px;
  border-top: 1px solid #d3d6e2;
  .grid-summary-inner {
    .summary-label {
      font-weight: 600;
    }
  }
}
.grid-summary-m {
  height: 49px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 2px solid #435c65;
  .button.grid-search-m {
    padding: 10px 15px;
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 0;
    height: 49px;
  }
  .grid-summary-inner {
    padding: 10px 15px;
    .summary-label {
      font-weight: 600;
    }
  }
}

html[dir="ltr"] {
  .grid-bottom {
    .per-page {
      .per-page-dropdown {
        .button-text-w {
          margin: 0 7px 0 0;
        }
      }
    }
    .grid-show {
      .show-text {
        margin: 0 5px 0 0;
      }
      .show-nav {
        .show-nav-button {
          transform: scaleX(-1);
        }
      }
    }
  }
}
</style>
